import React, { createContext, useContext, useState, useCallback } from 'react';
import adminService from '../services/adminService';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      setError('Failed to fetch admin statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (page = 1, limit = 20, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getUsers(page, limit, filters);
      if (result.success) {
        setUsers(Array.isArray(result.data) ? result.data : []);
        return result;
      } else {
        setUsers([]);
        setError(result.error || 'Failed to fetch users');
      }
    } catch (error) {
      setUsers([]);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchShops = useCallback(async (page = 1, limit = 20, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getShops(page, limit, filters);
      if (result.success) {
        setShops(Array.isArray(result.data) ? result.data : []);
        return result;
      } else {
        setShops([]);
        setError(result.error || 'Failed to fetch shops');
      }
    } catch (error) {
      setShops([]);
      setError('Failed to fetch shops');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getPlans();
      if (result.success) {
        setPlans(Array.isArray(result.data) ? result.data : []);
      } else {
        setPlans([]);
        setError(result.error || 'Failed to fetch plans');
      }
    } catch (error) {
      setPlans([]);
      setError('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async (page = 1, limit = 20, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getOrders(page, limit, filters);
      if (result.success) {
        setOrders(result.data);
        return result;
      }
    } catch (error) {
      setError('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async (page = 1, limit = 50, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getLogs(page, limit, filters);
      if (result.success) {
        setLogs(Array.isArray(result.data) ? result.data : []);
        return result;
      } else {
        setLogs([]);
        setError(result.error || 'Failed to fetch system logs');
      }
    } catch (error) {
      setLogs([]);
      setError('Failed to fetch system logs');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (userId, userData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.updateUser(userId, userData);
      if (result.success) {
        await fetchUsers();
        return result;
      }
    } catch (error) {
      setError('Failed to update user');
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const getUserById = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getUserById(userId);
      return result;
    } catch (e) {
      setError('Failed to fetch user');
      return { success: false, error: 'Failed to fetch user' };
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserSubscriptions = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.getUserSubscriptions(userId);
      return result;
    } catch (e) {
      setError('Failed to fetch subscriptions');
      return { success: false, error: 'Failed to fetch subscriptions' };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteUser = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.deleteUser(userId);
      if (result.success) {
        await fetchUsers();
      }
      // Always return a structured result so callers don't get undefined
      return result;
    } catch (error) {
      setError('Failed to delete user');
      return { success: false, error: 'Failed to delete user' };
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const updateShop = useCallback(async (shopId, shopData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.updateShop(shopId, shopData);
      if (result.success) {
        await fetchShops();
        return result;
      }
    } catch (error) {
      setError('Failed to update shop');
    } finally {
      setLoading(false);
    }
  }, [fetchShops]);

  const deleteShop = useCallback(async (shopId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.deleteShop(shopId);
      if (result.success) {
        await fetchShops();
        return result;
      }
    } catch (error) {
      setError('Failed to delete shop');
    } finally {
      setLoading(false);
    }
  }, [fetchShops]);

  const updatePlan = useCallback(async (planId, planData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.updatePlan(planId, planData);
      if (result.success) {
        await fetchPlans();
        return result;
      }
    } catch (error) {
      setError('Failed to update plan');
    } finally {
      setLoading(false);
    }
  }, [fetchPlans]);

  const createPlan = useCallback(async (planData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.createPlan(planData);
      if (result.success) {
        await fetchPlans();
        return result;
      }
    } catch (error) {
      setError('Failed to create plan');
    } finally {
      setLoading(false);
    }
  }, [fetchPlans]);

  const deletePlan = useCallback(async (planId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.deletePlan(planId);
      if (result.success) {
        await fetchPlans();
        return result;
      }
    } catch (error) {
      setError('Failed to delete plan');
    } finally {
      setLoading(false);
    }
  }, [fetchPlans]);

  const assignUserPlan = useCallback(async (userId, planId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminService.assignUserPlan(userId, planId);
      if (result.success) {
        await fetchUsers();
        return result;
      } else {
        setError(result.error || 'Failed to assign plan');
        return result;
      }
    } catch (error) {
      setError('Failed to assign plan');
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  // ============================================
  // TESTING METHOD - Remove after testing
  // ============================================
  const updateSubscriptionExpiry = useCallback(async (subscriptionId, expiresAt) => {
    try {
      const result = await adminService.updateSubscriptionExpiry(subscriptionId, expiresAt);
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to update subscription expiry' };
    }
  }, []);

  const value = {
    // State
    stats,
    users,
    shops,
    plans,
    orders,
    logs,
    loading,
    error,
    
    // Actions
    fetchStats,
    fetchUsers,
    fetchShops,
    fetchPlans,
    fetchOrders,
    fetchLogs,
    updateUser,
    deleteUser,
    updateShop,
    deleteShop,
    updatePlan,
    createPlan,
    deletePlan,
    assignUserPlan,
    getUserById,
    getUserSubscriptions,
    updateSubscriptionExpiry,
    
    // Computed
    recentUsers: users?.slice(0, 5),
    recentShops: shops?.slice(0, 5),
    recentOrders: orders?.slice(0, 5)
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
